import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';
import { klona } from 'klona';

// store
import { storeSet } from '../../store';

// components
import FormField from '../FormField';
import ButtonFlat from '../ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../ButtonWrapper/components/ButtonFormSubmit';
import ModalDialog from '../ModalDialog';
import { cueSheetTypes } from '../../configData';
import DownloadIcon from '../../icons/DownloadIcon';

/**
 * Columns the import endpoint reads: `type`, `label` and `title` are required,
 * `timecode` (HH:MM:SS:FF) is optional. Types: MUSIC, SFX, MIX, REFERENCE.
 */
const buildCsvTemplate = (cueType) => [
    'type,label,title,timecode',
    `${cueType},1m1,Opening theme,00:00:05:00`,
    `${cueType},1m2,Chase,00:01:12:12`,
    `${cueType},1m3,End credits,`,
].join('\n');

const downloadCsvTemplate = (cueType) => {
    const url = window.URL.createObjectURL(new Blob([buildCsvTemplate(cueType)], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cue-sheet-template-${cueType.toLowerCase()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

const ModalAddCueSheetItem = ({
                                  projectId,
                                  type,
                                  headerText = __('Aggiungi alla CueSheet/SoundList', 'soundbetween')
                              }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const {
        control,
        handleSubmit,
        watch,
        formState: { isValid },
        reset
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            label: '',
            title: '',
            image: null
        }
    });

    const fileValue = watch('image');
    const cueType = cueSheetTypes[type] || cueSheetTypes.music;

    const onSubmit = async (formData) => {
        setLoading(true);

        try {
            if (formData.image) {
                // File import workflow: send only file
                const formDataToSend = new FormData();
                formDataToSend.append('file', formData.image);
                await storeSet('importCueSheetItems', projectId, formDataToSend);
            } else {
                // Manual entry workflow: send JSON
                let updatedFormData = klona({ ...formData });
                updatedFormData.cue_type = cueType;
                updatedFormData.timecode = '00:00:00:00';
                delete updatedFormData.image;

                await storeSet('createCueSheetItem', projectId, updatedFormData);
            }

            reset();
            setModalVisible(false);
        } catch (error) {
            // Error already displayed by store action
        } finally {
            setLoading(false);
        }
    }

    const handleCreateNew = () => {
        setModalVisible(true);
    }

    const handleCloseModal = () => {
        reset();
        setModalVisible(false);
    }

    return (
        <>
            <ButtonFlat
                onClick={handleCreateNew}>
                {__('+ Aggiungi', 'soundbetween')}
            </ButtonFlat>
            {modalVisible
                ? <ModalDialog
                    additionalClasses={['modalCreateProject']}
                    header={headerText}
                    visible={modalVisible}
                    onSubmit={handleSubmit(onSubmit)}
                    onHide={handleCloseModal}>
                    <form className="appForm formInModal" onSubmit={handleSubmit(onSubmit)}>
                        <FormField
                            type="text"
                            name="label"
                            label={__('Label', 'soundbetween')}
                            placeholder={__('es 1m3', 'soundbetween')}
                            control={control}
                            rules={{
                                required: !fileValue ? __('È obbligatorio', 'soundbetween') : false
                            }}
                        />

                        <FormField
                            control={control}
                            name="title"
                            label={__('Titolo', 'soundbetween')}
                            placeholder="Titolo"
                            type="text"
                            rules={{
                                required: !fileValue ? __('È obbligatorio', 'soundbetween') : false
                            }}
                        />

                        <div className="appForm__heading">
                            <p>{__('Oppure Carica file', 'soundbetween')}</p>
                            <span>{__('File supportati: .csv, .xlsx', 'soundbetween')}</span>
                            <span>
                                {__('Colonne obbligatorie: type, label, title. Facoltativa: timecode (HH:MM:SS:FF). Valori di type: MUSIC, SFX, MIX, REFERENCE.', 'soundbetween')}
                            </span>
                            <button
                                type="button"
                                className="appForm__templateLink"
                                onClick={() => downloadCsvTemplate(cueType)}>
                                <DownloadIcon/>
                                {__('Scarica CSV di esempio', 'soundbetween')}
                            </button>
                        </div>

                        <FormField
                            control={control}
                            name="image"
                            label={__('Oppure carica file', 'soundbetween')}
                            placeholder={__('Scegli un file', 'soundbetween')}
                            type="fileupload"
                            accept=".csv, .xlsx"
                            rules={{
                                required: false
                            }}
                        />

                        <div className="appFormActions">
                            <ButtonFlat size="lg" type="button" onClick={handleCloseModal}>
                                {__('Annulla', 'soundbetween')}
                            </ButtonFlat>
                            <ButtonFormSubmit disabled={loading || !isValid}>
                                {__('Salva e continua', 'soundbetween')}
                            </ButtonFormSubmit>
                        </div>
                    </form>
                </ModalDialog> : null}
        </>
    )
}

export default ModalAddCueSheetItem;